import Api from 'services/Api';
import store from 'store/index';
import {previousValid} from 'store/generators/step';
import Validator from 'services/Validator';
import {requestPayload} from 'store/modules/claim';
import {generalDamagesTemplate} from 'store/modules/shared/templates';
import _ from 'lodash';
import BigNumber from 'bignumber.js';

export const mutationTypes = {
    ADD_AWARD: 'ADD_AWARD',
    UPDATE_TREE: 'UPDATE_TREE',
    UPDATE_AWARD: 'UPDATE_AWARD',
    REMOVE_AWARD: 'REMOVE_AWARD',
    RESET: 'RESET',
    LOAD: 'LOAD'
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'awards' : {type: 'array', rules: 'required'},
};

function initialState() {
    return {
        tree: null,
        awards: [],
    };
}
function jcGuidelinesInfo(state, index) {
    if (!state.awards[index].path.length) {
        return;
    }

    let path = state.awards[index].path.slice(0);
    let branch = state.tree[path[0]];

    if (!branch) {
        return;
    }

    let item = branch.chapter;

    path.splice(0, 1);

    let result = _.reduce(path, (result, value, key) => {
        branch = branch.children[value];
        result.push(branch.index);

        return result;
    }, []);

    result.unshift(item);

    return result.toString();
}

function jcGuidelinesNames(state, index) {
    if (!state.awards[index].path.length) {
        return;
    }

    let path = state.awards[index].path.slice(0);
    let branch = state.tree[path[0]];

    if (!branch) {
        return;
    }

    path.splice(0, 1);

    let result = _.reduce(path, (result, value, key) => {
        branch = branch.children[value];
        result.push(branch.name );

        return result;
    }, []);

    return result.toString();
}

function awardsValid(state) {
    if (!state.awards.length) {
        return;
    }

    let awards = state.awards.map(award => {
        return award.generalDamages > 0
    });

    return awards.reduce((valid, value) => valid ? (value === true) : false, true);
}

function discountsValid(state) {
    if (!state.awards.length) {
        return;
    }

    let awards = state.awards.map(award => {
        return (award.discount >= 0 && award.discount <= 100 && award.discount !== '');
    });

    return awards.reduce((valid, value) => valid ? (value === true) : false, true);
}

// Initial State
const state = initialState();

// Getters
const getters = {
    getAward: state => (index) => state.awards[index],
    getTree: state => state.tree,
    getGeneralDamages: state => index => state.awards[index].generalDamages,
    getPath: state => index => state.awards[index].path,
    getJCGuidelinesInfo: state => (index) => jcGuidelinesInfo(state, index),
    getJCGuidelinesNames: state => (index) => jcGuidelinesNames(state, index),
    getAwards: state => state.awards,
    getUplift: state => index => state.awards[index].uplift,
    getAwardsValid: state => awardsValid(state),
    getDiscountsValid: state => discountsValid(state),
    getSubTotal: state => state.awards.reduce((carry, award) => carry + award.generalDamages, 0),
    getSubTotalDiscounted: state => state.awards.reduce((carry, award) => carry + award.total, 0),
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     */
    save({getters, dispatch, state}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = {
            awards: state.awards,
        };

        Api.claims.save(payload).then((response) => {
            return response;
        });

        store.dispatch('personal/save', requestPayload(2));
    },
    /**
     * Loads the step being received from the Server, to be placed in the state.
     */
    load({ dispatch, action, commit}, step) {
        const data = [];

        _.map(step.data.awards, (award, key) => {
           return data.push(_.mapKeys(award, (value, key) => {
                return _.camelCase(key);
            }));
        });

        commit(mutationTypes.LOAD, {key: 'awards', value: data});
    },
    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({ dispatch }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 1,
            valid: true, // needs updating with real validity
        });
    },
    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({ commit, rootState, state }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 1,
            valid: previousValid(rootState, 1)
            && (new Validator(state, rules)).validate(),
        });
    },
    /**
     * Resets the step back to defaults
     */
    reset({ commit }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 1,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },
    /**
     * Update a specific item in the state
     */
    updateValue({ commit }, { key, value, index }) {
        commit(mutationTypes.UPDATE_AWARD, { key, value, index });
    },
    /**
     * Update a specific item in the state
     */
    updateTree({ commit }, { key, value }) {
        commit(mutationTypes.UPDATE_TREE, { key, value });
    },
    /**
     * Fetch the injury awards and add to state
     */
    fetchInjuries({ dispatch }) {
        return Api.personalInjury.generalDamages.injuries()
            .then(data => {
                dispatch('updateTree', {key: 'tree', value: data});
                dispatch('addAwardCheck');
            })
    },
    /**
     * Check to see if awards needs to be added
     */
    addAwardCheck({ state, dispatch }) {
        if (!state.awards.length) {
            dispatch('addAward');
        }
    },
    /**
     * Add an award
     */
    addAward({ commit }) {
        commit(mutationTypes.ADD_AWARD);
    },
    /**
     * Remove an award
     */
    removeAward({ commit, dispatch }, index) {
        commit(mutationTypes.REMOVE_AWARD, index);
    }
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_AWARD](state, payload) {
        let award = state.awards[payload.index];
        if (!award) return;
        award[payload.key] = payload.value;
        let gd = BigNumber(award.generalDamages || 0);
        let discount = BigNumber(1).minus(
            BigNumber(award.discount || 0).dividedBy(100)
        );
        let total = gd.times(discount);

        award.total = +total.toFixed(2);
    },
    [mutationTypes.UPDATE_TREE](state, payload) {
        state.tree = payload.value;
    },
    [mutationTypes.ADD_AWARD](state) {
        state.awards.push(generalDamagesTemplate())
    },
    [mutationTypes.REMOVE_AWARD](state, index) {
        state.awards.splice(index, 1);
    },
    [mutationTypes.LOAD](state, payload) {
        state.awards = payload.value;
    },
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/personal/generalDamages.js